import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import { useClickOutside } from "@/Common/CustomHooks/useClickOutside";
import useDebounce from "@/Common/CustomHooks/useDebounce";
import { useKeyHold } from "@/Common/CustomHooks/useKeyHold";
import { useEffectAfterMount } from "../CustomHooks/useEffectAfterMount";
import { LoadingIcon } from "./IconControls/LoadingIcon";

import styles from "./SearchTextControl.module.scss";

interface ISearchComponentProps<TItem> {
    /**
     * Async callback function that returns the list of matched items
     * @param searchText The search text used to execute the search
     * @returns The list of matched items.
     */
    onSearch: (searchText: string) => Promise<TItem[]>;

    /**
     * Render function for each item in the result list
     */
    renderItem: (item: TItem) => JSX.Element;

    /**
     * Callback function to ge the id for the specific item
     */
    getItemId: (item: TItem) => string;

    /**
     * Callback function triggered when we select a specific item. Either by using the keyboard or clicking on the item
     */
    onItemSelected: (item: TItem) => void;

    /**
     * Callback function triggered when we type text in the input field
     */
    onTextChange: (value: string) => void;

    /**
     * Debounce time in milliseconds for onSearch callback execution.
     * @default 500
     */
    debouceInMs?: number;

    /**
     * If true, the search field will be cleared when selecting an item.
     * @default true
     */
    clearSearchTextOnSelect?: boolean;

    /**
     * The CSS class name of the selected item.
     * @default styles.selected
     */
    selectedItemCssClass?: string;

    /**
     * The search text received from outside the component.
     */
    searchText?: string;

    /**
     * If the entered value is valid.
     */
    isValid?: boolean;

    /**
     * Search result when the search text is empty.
     */
    defaultSearchResult?: TItem[];

    /**
     * The current search result.
     * If null or undefined, the search result remains unchanged.
     * Set this prop to an empty array to clear the search result.
     */
    searchResultOverride?: TItem[];

    /**
     * If the input field is disabled or not.
     */
    disableSearchField?: boolean;

    /**
     * Wether the automatic scroll to the search result should be disabled or not.
     */
    scrollToSearchResult?: boolean;

    /**
     * The render function for the header of a section.
     */
    renderSectionHeader?: (sectionId: number | string) => JSX.Element;

    /**
     * The render function for sections that contain children.
     */
    renderSection?: (sectionId: number | string, children?: JSX.Element[]) => JSX.Element;

    /**
     * The function that returns the section id for a given item.
     * The section with the lowest id will be rendered first.
     */
    getSectionId?: (item: TItem) => string;

    /**
     * The maximum number of characters used for the search.
     */
    maxLength?: number;

    /*
    * Custom attribute we set to be able to select this component during testing.
    */
    dataCy: string;

    /**
     * The optional placeholder text for the input field
     */
    placeholder?: string;

    /**
     * The CSS id of the input.
     */
    id?: string;

    /** If true, the dropdown position is fixed to the input field width and position.
     * This is useful when the input field is in a modal.
     */
    isDropdownPositionFixed?: boolean;

    /** The maximum height of the dropdown in pixels.
     * If not set, the dropdown will have no maximum height.
     */
    dropDownMaxHeight?: number;

    /** If true, the dropdown will be rendered above the input field.
     * This is useful when the input field is close to the bottom of the screen.
     */
    renderDropdownAboveInput?: boolean;
}

const itemIdPrefix = "search-result-item-";

/**
 * Exposed functions that the SearchTextControl exposes for external use
 */
interface ISearchControlExposedFunctions<TItem> {
    /**
     * Resets the search state of the control, clearing any entered text and search results.
     */
    resetSearch: () => void;

    /**
     * Triggers the search functionality of the control, initiating a search based on the entered text.
     */
    triggerSearch: () => void;

    /**
     * Allows for custom selection of an item within the control, typically used for programmatically selecting an item.
     * @param item The item to be selected.
     */
    customSelectItem: (item: TItem) => void;
}

/**
 * This component is used to trigger a search asyncally and shows the result below the input field.
 * Keyboard keys up and down can be used to select the item and clicking enter is used to trigger the selection callback.
 * The different functions can be used to customize the view of each item in the list, the sections can also be defined.
 */
function SearchTextControlInner<TItem extends unknown>(
    props: ISearchComponentProps<TItem>,
    ref: React.MutableRefObject<ISearchControlExposedFunctions<TItem>>
): JSX.Element {
    const [searchText, setSearchText] = useState<string>(props.searchText ?? "");
    const [shouldDisplayResult, setShouldDisplayResult] = useState<boolean>(false);
    const [cursor, setCursor] = useState<number>(0);
    const [searchResult, setSearchResult] = useState<TItem[]>(props.searchResultOverride ?? props.defaultSearchResult ?? []);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [defaultSearchResult, setDefaultSearchResult] = useState<TItem[]>(props.defaultSearchResult ?? []);

    // props with default values
    const debouceInMs = useMemo<number>(() => props.debouceInMs ?? 500, [props.debouceInMs]);
    const clearSearchTextOnSelect = useMemo<boolean>(() => props.clearSearchTextOnSelect ?? true, [props.clearSearchTextOnSelect]);
    const selectedItemCssClass = useMemo<string>(() => props.selectedItemCssClass ?? styles.selected, [props.selectedItemCssClass]);

    const { getItemId, getSectionId, onSearch, renderItem, renderSection, onItemSelected, renderSectionHeader } = props;

    // sections
    const [sortedSectionIds, searchResultsBySection] = useMemo(
        () => {
            if (!getSectionId)
                return [null, null];

            const sectionIds = new Set<string>();
            const searchResultsBySection = searchResult.toLookup(p => {
                const sectionId = getSectionId(p);
                sectionIds.add(sectionId);
                return sectionId;
            });
            return [Array.from(sectionIds).sort((a, b) => a.localeCompare(b)), searchResultsBySection];
        },
        [searchResult, getSectionId]
    );

    const itemIndexesById = useMemo(
        () => {
            const indexes = {};
            if (!sortedSectionIds)
                searchResult.forEach((item, index) => indexes[getItemId(item)] = index);
            else {
                let index = 0;
                for (let sectionId of sortedSectionIds) {
                    const items = searchResultsBySection[sectionId];
                    for (let item of items)
                        indexes[getItemId(item)] = index++;
                }
            }
            return indexes;
        },
        [searchResult, getItemId, sortedSectionIds, searchResultsBySection]
    );

    const dropdownStyle = useCallback(() => {
        const style: React.CSSProperties = {};

        if (props.isDropdownPositionFixed) {
            style["position"] = "fixed";
            style["width"] = containerRef.current?.offsetWidth;
            if (props.renderDropdownAboveInput) {
                const containerRect = containerRef.current?.getBoundingClientRect();
                style["bottom"] = containerRect ? `${window.innerHeight - containerRect.top}px` : "auto";
            }
        }

        if (props.dropDownMaxHeight) {
            style["maxHeight"] = `${props.dropDownMaxHeight}px`;
        }

        return style;
    }, [props.isDropdownPositionFixed, props.dropDownMaxHeight]);

    const debouncedValue = useDebounce<string>(searchText, debouceInMs);

    const resultDivRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useClickOutside(containerRef, () => closeSearchResultDropdown());

    const closeSearchResultDropdown = (): void => {
        setShouldDisplayResult(false);
        setCursor(0);
    };

    useEffectAfterMount(() => setDefaultSearchResult(props.defaultSearchResult ?? []), [props.defaultSearchResult]);

    const resetSearch = (): void => {
        setSearchText("");
    };

    const selectItem = useCallback(
        (item: TItem): void => {
            closeSearchResultDropdown();

            if (clearSearchTextOnSelect)
                resetSearch();

            onItemSelected?.(item);
        },
        [clearSearchTextOnSelect, onItemSelected]
    );

    // keyboard navigation
    useKeyHold(
        "ArrowDown",
        () => {
            setCursor((prevCursor: number) => {
                if (searchResult.any() && prevCursor < searchResult.length - 1) {
                    scrollToItem(prevCursor + 1);
                    return prevCursor + 1;
                }
                return prevCursor;
            });
        },
        100,
        500,
        [searchResult?.length]
    );

    useKeyHold(
        "ArrowUp",
        () => {
            setCursor((prevCursor: number) => {
                if (searchResult.any() && prevCursor > 0) {
                    scrollToItem(prevCursor - 1);
                    return prevCursor - 1;
                }
                return prevCursor;
            });
        },
        100,
        500,
        [searchResult.any()]
    );

    async function search(): Promise<void> {
        setIsLoading(true);
        const data = await onSearch(debouncedValue);
        setSearchResult(data);
        setIsLoading(false);
        if (props.scrollToSearchResult)
            resultDivRef.current?.scrollIntoView({ behavior: "smooth" });
    }

    // search
    useEffectAfterMount(() => { !IsNullOrWhiteSpace(debouncedValue) && search(); }, [debouncedValue]);

    useEffect(
        () => {
            if (IsNullOrWhiteSpace(searchText) && !isLoading)
                setSearchResult(defaultSearchResult);
        },
        [searchText, defaultSearchResult, isLoading]
    );

    useEffect(() => { setSearchText(props.searchText ?? ""); }, [props.searchText]);

    useEffect(() => setCursor(0), [searchResult]);

    useEffect(() => props.searchResultOverride && setSearchResult(props.searchResultOverride), [props.searchResultOverride]);

    // rendering
    const renderWrappedItem = useCallback(
        (item: TItem, idx: number): JSX.Element => {
            const element = renderItem(item);

            if (!element)
                return null;

            return (
                <div
                    key={`${itemIdPrefix}${getItemId(item)}`}
                    id={`${itemIdPrefix}${idx}`}
                    data-cy={`${props.dataCy}-${idx}`}
                    className={`${cursor === idx ? selectedItemCssClass : ""} ${styles.searchItem}`}
                    onClick={() => selectItem(item)}
                    onMouseMove={() => setCursor(idx)}
                >
                    {element}
                </div>
            );
        },
        [cursor, selectedItemCssClass, getItemId, renderItem, selectItem]
    );

    const renderWrappedSection = useCallback(
        (sectionId: number | string, children: JSX.Element[]): JSX.Element => {
            const section = renderSection?.(sectionId, children);

            if (!section)
                return null;

            return (<div key={`section-${sectionId}`}>{section}</div>);
        },
        [renderSection]
    );

    const renderWrappedSectionHeader = useCallback(
        (sectionId: number | string): JSX.Element => {
            const section = renderSectionHeader?.(sectionId);

            if (!section)
                return null;

            return (<div key={`sectionHeader-${sectionId}`}>{section}</div>);
        },
        [renderSectionHeader]
    );

    useImperativeHandle(ref, () => ({
        resetSearch: resetSearch,
        triggerSearch: () => search(),
        customSelectItem: (item: TItem) => selectItem(item)
    }));

    return (
        <div className={`${styles.container} + ${props.isValid ? "" : " has-error"}`} ref={containerRef}>
            <input
                type="search"
                value={searchText}
                onChange={e => {
                    setSearchText(e.target.value);
                    setShouldDisplayResult(true);
                    props.onTextChange(e.target.value);
                }}
                onKeyDown={e => {
                    if (e.key === "Enter" && searchResult.any() && !isLoading)
                        selectItem(searchResult[cursor]);
                }}
                onFocus={() => setShouldDisplayResult(true)}
                className="form-control"
                disabled={props.disableSearchField}
                maxLength={props.maxLength}
                data-cy={props.dataCy}
                placeholder={props.placeholder}
                id={props.id}
            />
            {isLoading && shouldDisplayResult && (
                <div className={styles.loadingContainer}>
                    <LoadingIcon />
                </div>
            )}
            {shouldDisplayResult && !isLoading && (
                <div className={styles.listContainer} style={dropdownStyle()} ref={resultDivRef}>
                    {sortedSectionIds?.map(sectionId => {
                        const children = searchResultsBySection[sectionId].map(item => renderWrappedItem(item, itemIndexesById[getItemId(item)]));
                        const section = renderWrappedSection(sectionId, children);
                        return (
                            <React.Fragment key={sectionId}>
                                {renderWrappedSectionHeader(sectionId)}
                                {section}
                                {!section && children}
                            </React.Fragment>
                        );
                    })}
                    {!sortedSectionIds && searchResult.map(renderWrappedItem)}
                </div>
            )}
        </div>
    );
}

function scrollToItem(index: number): void {
    const element = document.getElementById(`${itemIdPrefix}${index}`);
    element?.scrollIntoView({ block: "nearest" });
}

const SearchTextControl = React.forwardRef(SearchTextControlInner) as (
    <TItem>(props: ISearchComponentProps<TItem>
    & { ref?: React.MutableRefObject<ISearchControlExposedFunctions<TItem>> }) => ReturnType<typeof SearchTextControlInner>
);

export { ISearchControlExposedFunctions, SearchTextControl };
